/**
 * Returns a function, that, when invoked, will only be triggered at most once during
 * a given window of time.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Time window (in milliseconds).
 * @returns {Function}
 */
function throttle(fn, wait = 300) {
  let throttleTimeoutId = -1;
  let tick = false;

  return () => {
    clearTimeout(throttleTimeoutId);
    throttleTimeoutId = setTimeout(fn, wait);

    if (!tick) {
      fn.call();
      tick = true;
      setTimeout(() => {
        tick = false;
      }, wait);
    }
  };
}

if (!customElements.get('sticky-atc')) {
  customElements.whenDefined('product-form').then(() => {
    class StickyAtc extends customElements.get('product-form') {
      constructor() {
        super();

        this.section = this.closest('.section-main-product');
        this.mainProductForm = this.section.querySelector('product-form');
        this.mainProductSubmitForm = this.mainProductForm.getSubmitForm();
        this.submitForm = this.getSubmitForm();
        this.buyButtons = this.section.querySelector('buy-buttons');
        this.imageContainer = this.querySelector('.sticky-atc__image');
        this.variantTitle = this.querySelector('.sticky-atc__details__variant__title');
        this.variantTitle?.toggleAttribute('hidden', !this.section.querySelector('variant-picker'));
        this.button = this.querySelector('.add-to-cart');

        // Matches the theme's mobile breakpoint used elsewhere in sticky-atc.css.
        this.mobileQuery = window.matchMedia('(max-width: 749px)');

        this.throttledOnScroll = throttle(this.handleScroll.bind(this));
        window.addEventListener('scroll', this.throttledOnScroll);

        this.throttledOnResize = throttle(this.handleResize.bind(this), 150);
        window.addEventListener('resize', this.throttledOnResize);

        // Set initial top offset + initial visibility
        this.updateStickyTopOffset();
        this.handleScroll();

        // Mobile only: the bar reverts from fixed-to-viewport back to its
        // own natural place in the document flow — exactly where
        // sticky-atc.liquid's render call already puts it: right after
        // the real inline <product-form> (price, size, buy button, and
        // every product-info accordion — Care / Delivery / Returns &
        // Exchanges — all live inside it) and before "You may also like".
        // Genuinely reverting to position: static there, not flying it in
        // with position: absolute and a computed top offset, so it can
        // never overlap page content above or below it — Jonathan was
        // explicit that a computed offset isn't good enough here, it has
        // to actually sit in the DOM flow. Falls back to .related-products
        // if a product template doesn't render an inline <product-form>
        // for some reason. See updateRelease().
        this.releaseTarget =
          this.section.querySelector('product-form') || document.querySelector('.related-products');

        this.updateRelease();
        if (typeof ResizeObserver !== 'undefined') {
          // Re-check on any change to the bar's own rendered height (text
          // wrapping to a second line, a web font swapping in, a different
          // variant's size text) since that changes the release point too.
          this.resizeObserver = new ResizeObserver(() => this.updateRelease());
          this.resizeObserver.observe(this);
        }

        this.section.addEventListener('on:media-gallery:change', this.updateImage.bind(this));

        // When no variant is selected (select_first_variant is off and the
        // customer hasn't chosen a size yet), sticky-atc.liquid renders a
        // plain <a href="#product-info"> here instead of a submit button.
        // The site-wide hash-link handler (main.js's internalLinksSmoothScroll)
        // picks that click up too, but it skips scrolling whenever its own
        // "already in view" check passes — and on initial page load the
        // #product-info anchor (an empty, zero-height marker placed right
        // above the variant picker) genuinely does sit within that check's
        // idea of "visible", even though the actual size buttons below it
        // are cut off by the viewport edge / this bar's own fixed footprint.
        // Handle it ourselves instead: always scroll, and target the real
        // <variant-picker> so the size options themselves end up in view,
        // not just the empty anchor above them.
        this.viewDetailsLink = this.querySelector('.sticky-atc__buttons > a');
        this.viewDetailsLink?.addEventListener('click', (evt) => {
          evt.preventDefault();
          const target = this.section.querySelector('variant-picker') || this.section.querySelector('#product-info');
          if (!target) return;
          window.scrollTo({
            top: theme.getOffsetTopFromDoc(target) - theme.stickyHeaderHeight() - 20,
            behavior: 'smooth'
          });
        });

        this.updateImage();
        this.updateAddToCartButton();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.throttledOnScroll);
        window.removeEventListener('resize', this.throttledOnResize);
        this.resizeObserver?.disconnect();
        this.classList.remove('sticky-atc--released');
      }

      /**
       * Handles window resize: keeps the sticky header offset and the
       * release point current (viewport height and the target section's
       * position both change on resize/orientation change).
       */
      handleResize() {
        this.updateStickyTopOffset();
        this.updateRelease();
      }

      /**
       * Mobile only: hands the bar off from position: fixed (docked to
       * the viewport bottom) to position: static (its own ordinary place
       * in the document flow, immediately after releaseTarget — see the
       * constructor) once there's enough room there that it no longer
       * needs flying down to stay visible. No document-relative offset
       * is computed at all: the CSS for .sticky-atc--released just turns
       * off position/inset entirely and lets the browser lay it out like
       * any other block, exactly where it already sits in the DOM — so
       * it's structurally impossible for it to overlap releaseTarget or
       * anything after it. Scrolling back up re-docks it to the viewport
       * bottom. No-op on desktop or if releaseTarget isn't present at
       * all — the bar then simply stays permanently fixed, today's
       * behaviour.
       */
      updateRelease() {
        if (!this.mobileQuery.matches || !this.releaseTarget) {
          this.classList.remove('sticky-atc--released');
          return;
        }

        const barHeight = this.offsetHeight;
        const releaseLine = window.innerHeight - barHeight;
        // Bottom edge of releaseTarget, not top: we're asking "has the
        // content that comes before the bar already scrolled clear of
        // where the fixed dock would sit?", not "has releaseTarget
        // itself reached that line".
        const targetBottom = this.releaseTarget.getBoundingClientRect().bottom;
        const shouldRelease = targetBottom <= releaseLine;

        this.classList.toggle('sticky-atc--released', shouldRelease);
      }

      /**
       * Handles submission of the product form.
       * @param {object} evt - Event object.
       */
      async handleSubmit(evt) {
        evt.preventDefault();

        const variantId = this.section.querySelector('.js-product-form input[name="id"]').value;
        if (!variantId) {
          const target = this.section.querySelector('#product-info');
          if (target && target.offsetParent) {
            evt.preventDefault();
            theme.scrollToRevealElement(target);
          }
          return;
        }

        // Validate main form
        const customFormValid = this.mainProductForm.validate();
        if (!customFormValid || !this.mainProductSubmitForm.reportValidity()) {
          evt.preventDefault();
          if (!customFormValid) {
            theme.scrollToRevealElement(this.mainProductForm);
          } else {
            const input = Array.from(this.mainProductSubmitForm.elements).find(
              (el) => !el.checkValidity()
            );
            setTimeout(() => theme.scrollToRevealElement(input), 100);
          }
          return;
        }

        // Clear copied data
        this.submitForm.querySelectorAll('[data-copied]').forEach((el) => el.remove());

        // Copy data
        const formData = new FormData(this.mainProductSubmitForm);
        for (const p of formData) {
          if (!this.submitForm.querySelector(`[name="${p[0]}"]`)) {
            const input = document.createElement('input');
            input.name = p[0];
            input.value = p[1];
            input.hidden = true;
            input.setAttribute('data-copied', '');
            this.submitForm.append(input);
          }
        }
        super.handleSubmit(evt);
      }

      /**
       * Updates CSS variable for sticky header height and returns the current offset.
       * Used for positioning sticky ATC under the header and for scroll visibility logic.
       * @returns {number}
       */
      updateStickyTopOffset() {
        let h = 0;

        // Prefer theme helper (accounts for announcement + sticky header behaviour)
        if (typeof theme !== 'undefined' && typeof theme.stickyHeaderHeight === 'function') {
          h = theme.stickyHeaderHeight();
        } else {
          const header = document.querySelector('.section-header');
          h = header ? header.offsetHeight : 0;
        }

        document.documentElement.style.setProperty('--sticky-header-height', `${h}px`);
        return h;
      }

      /**
       * Determine visibility after scroll.
       */
      handleScroll() {
        const topOffset = this.updateStickyTopOffset();

        // Mobile: bar is permanently visible from page load, never
        // scroll-triggered. Keep it out of the sticky-atc--out /
        // scrolled-to-bottom hide states entirely (CSS also forces
        // this, but skip the class churn here too).
        if (this.mobileQuery.matches) {
          this.classList.remove('sticky-atc--out');
          document.body.classList.remove('scrolled-to-bottom');
          this.updateRelease();
          return;
        }

        // Prefer the dedicated anchor (inserted by Liquid when sticky ATC is enabled).
        // Fall back to buy-buttons if the anchor isn't present.
        const anchor = this.section.querySelector('#product-info');
        const triggerEl = anchor || this.buyButtons;

        if (triggerEl && typeof triggerEl.getBoundingClientRect === 'function') {
          const rect = triggerEl.getBoundingClientRect();
          // Show the sticky ATC once the trigger has scrolled above the sticky header.
          const shouldShow = rect.top <= topOffset;
          // sticky-atc--out = hidden
          this.classList.toggle('sticky-atc--out', !shouldShow);
        }

        document.body.classList.toggle(
          'scrolled-to-bottom',
          window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
        );
      }

      /**
       * Updates the image.
       */
      updateImage() {
        const mainImage = this.section.querySelector('.media-gallery .main-image .slider__item.is-active .product-media img');

        if (mainImage) {
          this.imageContainer.innerHTML = mainImage.outerHTML;
        } else {
          this.imageContainer.textContent = '';
        }
      }

      /**
       * Updates the add to cart button text.
       */
      updateAddToCartButton() {
        // If there is no variant selected, show unavailable-text
        const variantId = this.section.querySelector('.js-product-form input[name="id"]').value;
        if (!variantId) {
          this.button.innerHTML = this.button.dataset.unavailableText;
        }
      }
    }

    customElements.define('sticky-atc', StickyAtc);
  });
}